import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarRange, TrendingUp, TrendingDown } from "lucide-react";
import api, { TRY, NUM, trDate } from "@/lib/api";
import { Card } from "@/components/Cards";

const shiftMonth = (m, n) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (m) => new Date(`${m}-01`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

export default function MonthlyPurchaseReport({ refreshKey }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/monthly-purchases", { params: { month } }).then((r) => setData(r.data)); }, [month, refreshKey]);

  return (
    <Card className="p-5" data-testid="monthly-purchase-report">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><CalendarRange className="h-5 w-5 text-primary" /> Aylık Alım Raporu</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{monthLabel(month)} · {data?.suppliers?.length ?? 0} tedarikçi · <span className="font-mono">{NUM(data?.total_qty ?? 0)}</span> birim</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ay Toplamı</div><div className="font-mono text-xl font-bold text-rose-400" data-testid="monthly-purchase-total">{TRY(data?.total)}</div></div>
          <div className="flex items-center gap-1">
            <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="monthly-purchase-prev" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="monthly-purchase-month" className="h-9 w-40 rounded-md border border-input bg-background px-2 text-sm font-mono" />
            <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="monthly-purchase-next" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      {!data?.suppliers?.length ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="monthly-purchase-empty">Bu ay günlük alım kaydı yok</div> : (
        <div className="space-y-4">
          {data.suppliers.map((s) => (
            <div key={s.supplier_id} className="rounded-xl border border-border overflow-hidden" data-testid={`monthly-purchase-supplier-${s.supplier_id}`}>
              <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40">
                <div><span className="font-semibold">{s.supplier_name}</span><span className="text-xs text-muted-foreground ml-2">{s.days} gün alım</span></div>
                <span className="font-mono font-bold">{TRY(s.total)}</span>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="px-4 py-2">Ürün</th><th className="px-4 py-2 text-right">Miktar</th><th className="px-4 py-2 text-right">Ort. Fiyat</th><th className="px-4 py-2 text-right">Fiyat Aralığı</th><th className="px-4 py-2 text-right">Tutar</th>
                </tr></thead>
                <tbody>
                  {s.products.map((p) => (
                    <tr key={p.product_id} className="border-b border-border/40 last:border-0" data-testid={`monthly-purchase-product-${p.product_id}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{p.name}</div>
                        {p.price_changes.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {p.price_changes.map((c, i) => { const up = c.to > c.from; return (
                              <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono ${up ? "bg-rose-400/10 text-rose-400" : "bg-emerald-400/10 text-emerald-400"}`} data-testid={`price-change-${p.product_id}-${i}`}>
                                {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{trDate(c.date)}: {TRY(c.from)} → {TRY(c.to)}
                              </span>
                            ); })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">{NUM(p.qty)} <span className="text-xs text-muted-foreground">{p.unit}</span></td>
                      <td className="px-4 py-2 text-right font-mono">{TRY(p.avg_price)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-muted-foreground">{p.min_price === p.max_price ? TRY(p.min_price) : `${TRY(p.min_price)} – ${TRY(p.max_price)}`}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">{TRY(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
